import { Box, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { OwnerTypes } from '@/api/fund/types';
import {
  getTransactionDetailOptions,
  getTransactionFilterOptionsOptions,
  getTransactionsInfiniteOptions,
} from '@/api/transaction-views/queries';
import { formatTransactionType } from '@/features/transactions/utils/format-transaction-type';
import {
  applyCounterpartyDisplayNames,
  formatTransactionDirection,
  mapTransactionItems,
  type TransactionListFilters,
  type TransactionSortField,
} from '@/features/transactions/utils/transaction-list';
import useDebounce from '@/global-hooks/use-debounce';
import { Spinner, theme, Typography } from '@/shared/design-system';
import { useInfiniteScroll } from '@/shared/hooks/use-infinite-scroll';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

import { TransactionDetailModal } from './transaction-detail-modal';
import { TransactionFilters } from './transaction-filters';
import { TransactionLoadError } from './transaction-load-error';
import { buildTransactionQueryParams } from './transaction-query-params';
import { TransactionTable } from './transaction-table';
import { useTransactionCounterpartyDisplayNames } from './use-transaction-counterparty-display-names';

const initialFilters: TransactionListFilters = {
  searchTerm: '',
  dateRange: [null, null],
  direction: '',
  type: '',
  denomination: '',
  sortField: 'date',
  sortDirection: 'desc',
};

export interface MyTransactionsProps {
  onRefresh?: () => Promise<unknown> | void;
  isRefreshing?: boolean;
}

const MyTransactions = ({ onRefresh, isRefreshing = false }: MyTransactionsProps) => {
  const [filters, setFilters] = useState<TransactionListFilters>(initialFilters);
  const [selectedTransactionId, setSelectedTransactionId] = useState('');
  const { selectedUserRole, selectedEntityId } = useSelectedUserConfigSlice();
  const ownerFilters = useMemo(
    () => ({
      ownerType: selectedUserRole === 'entity' ? OwnerTypes.ENTITY : OwnerTypes.USER,
      ...(selectedUserRole === 'entity' && selectedEntityId ? { ownerId: selectedEntityId } : {}),
    }),
    [selectedEntityId, selectedUserRole],
  );
  const debouncedSearchTerm = useDebounce(filters.searchTerm, 300);
  const transactionQueryParams = useMemo(
    () =>
      buildTransactionQueryParams({ ...filters, searchTerm: debouncedSearchTerm }, ownerFilters),
    [debouncedSearchTerm, filters, ownerFilters],
  );

  const {
    items: transactionItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    loadMoreRef,
    isError,
    isFetchNextPageError,
    refetch: retryTransactionList,
    fetchNextPage: retryNextPage,
  } = useInfiniteScroll(
    getTransactionsInfiniteOptions(transactionQueryParams),
    (page) => page.transactions,
  );
  const { data: filterOptions = { directions: [], denominations: [], types: [] } } = useQuery(
    getTransactionFilterOptionsOptions(ownerFilters),
  );
  const {
    data: transactionDetail,
    isLoading: isTransactionDetailLoading,
    isError: isTransactionDetailError,
    refetch: retryTransactionDetail,
  } = useQuery({
    ...getTransactionDetailOptions(selectedTransactionId),
    enabled: Boolean(selectedTransactionId),
  });

  const serverTransactions = useMemo(
    () => mapTransactionItems(transactionItems),
    [transactionItems],
  );
  const counterpartyDisplayNames = useTransactionCounterpartyDisplayNames(serverTransactions);
  const transactions = useMemo(
    () => applyCounterpartyDisplayNames(serverTransactions, counterpartyDisplayNames),
    [counterpartyDisplayNames, serverTransactions],
  );
  const handleSort = useCallback((field: TransactionSortField) => {
    setFilters((current) => ({
      ...current,
      sortField: field,
      sortDirection:
        field === current.sortField && current.sortDirection === 'asc' ? 'desc' : 'asc',
    }));
  }, []);
  const handleSelect = useCallback((id: string) => setSelectedTransactionId(id), []);

  return (
    <Stack gap={`${theme.spacing[24]}px`} flexDirection="column" width="100%">
      <Typography variant="h4" fontWeight={600}>
        Transactions
      </Typography>

      <TransactionFilters
        filters={filters}
        directionOptions={filterOptions.directions.map((value) => ({
          value,
          label: formatTransactionDirection(value),
        }))}
        denominationOptions={filterOptions.denominations.map((value) => ({ value, label: value }))}
        typeOptions={filterOptions.types.map((value) => ({
          value,
          label: formatTransactionType(value),
        }))}
        onFiltersChange={setFilters}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
      />

      {isError && transactionItems.length === 0 ? (
        <TransactionLoadError
          message="Unable to load transactions."
          onRetry={() => retryTransactionList()}
        />
      ) : (
        <TransactionTable
          transactions={transactions}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          sortField={filters.sortField}
          sortDirection={filters.sortDirection}
          onSort={handleSort}
          onSelect={handleSelect}
        />
      )}

      <Box
        ref={loadMoreRef}
        sx={{ minHeight: theme.spacing[20], display: 'flex', justifyContent: 'center' }}
      >
        {isFetchNextPageError ? (
          <TransactionLoadError
            message="Unable to load more transactions."
            onRetry={() => retryNextPage()}
          />
        ) : isFetchingNextPage ? (
          <Box role="status" aria-label="Loading more transactions">
            <Spinner />
          </Box>
        ) : null}
      </Box>

      <TransactionDetailModal
        open={Boolean(selectedTransactionId)}
        detail={transactionDetail}
        isLoading={isTransactionDetailLoading}
        isError={isTransactionDetailError}
        counterpartyDisplayNames={counterpartyDisplayNames}
        onRetry={() => retryTransactionDetail()}
        onClose={() => setSelectedTransactionId('')}
      />
    </Stack>
  );
};

export default MyTransactions;
